/**
 * FR-018.7 — one row of the Sources section (TENANT_CONFIGURATION_UI.md §2).
 *
 * Properties-grid rhythm, three cells: `label · value (+ 12 px meta line) · trailing`.
 * Colour only on the rows that need a decision; confirmed rows carry no badge and no
 * confidence number is ever shown.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SourceStatusBadge } from '@hofsync/design-system';
import type { SourceRowDto, SourcesConfig, SourcesMaster } from '../../../../services/sourcesService';
import { formatDateString } from '../../formatValue';
import { validityPhrase } from './validity';

export type SourceEditorMode = 'change' | 'assign' | 'accept' | 'add';

type T = (key: string, def: string, opts?: Record<string, unknown>) => string;

/** `meta` parts joined by ` · `, dropping empty ones so a row never shows `· ·` or a dangling dot. */
function joinMeta(parts: Array<string | null | undefined | false>): string {
    return parts.filter((p): p is string => typeof p === 'string' && p.trim() !== '').join(' · ');
}

function countsPhrase(counts: SourceRowDto['counts'] | undefined, t: T, lang: string): string {
    if (!counts) return '';
    const fmt = new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'de-DE', { maximumFractionDigits: 1 });
    return joinMeta([
        typeof counts.documents === 'number' &&
            t('farm.sources.counts.documents', '{{n}} Belege', { count: counts.documents, n: fmt.format(counts.documents) }),
        typeof counts.picks === 'number' &&
            t('farm.sources.counts.picks', '{{n}} Pflückungen', { count: counts.picks, n: fmt.format(counts.picks) }),
        typeof counts.kg === 'number' && t('farm.sources.counts.kg', '{{kg}} kg', { kg: fmt.format(counts.kg) }),
    ]);
}

/** Evidence is a `farm.sources.evidence.*` key when the server sends one, else a sentence as-is. */
function useEvidence(evidence: string | null | undefined): string | null {
    const { t, i18n } = useTranslation();
    if (!evidence) return null;
    const key = `farm.sources.evidence.${evidence}`;
    return /^[a-z_]+$/.test(evidence) && i18n.exists(key) ? String(t(key)) : evidence;
}

interface CellsProps {
    kind: string;
    label: React.ReactNode;
    value: React.ReactNode;
    meta?: React.ReactNode;
    meta2?: React.ReactNode;
    trailing?: React.ReactNode;
    dimmed?: boolean;
    /** A failed row action, as one muted sentence under the row. */
    error?: string | null;
}

const Cells: React.FC<CellsProps> = ({ kind, label, value, meta, meta2, trailing, dimmed, error }) => (
    <div className={`sources-row sources-row--${kind}${dimmed ? ' sources-row--dimmed' : ''}`} data-testid={`sources-row-${kind}`}>
        <span className="sources-row__label">{label}</span>
        <span className="sources-row__cell">
            <span className="sources-row__value">{value}</span>
            {meta ? <span className="sources-row__meta">{meta}</span> : null}
            {meta2 ? <span className="sources-row__meta sources-row__meta--evidence">{meta2}</span> : null}
        </span>
        <span className="sources-row__trailing">{trailing}</span>
        {error ? (
            <span className="sources-row__error" role="alert">
                {error}
            </span>
        ) : null}
    </div>
);

const Action: React.FC<{ onClick: () => void; primary?: boolean; disabled?: boolean; children: React.ReactNode }> = ({
    onClick,
    primary,
    disabled,
    children,
}) => (
    <button
        type="button"
        className={`sources-row__action${primary ? ' sources-row__action--primary' : ''}`}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </button>
);

// --- master / origin -------------------------------------------------------

export interface SourceMasterRowProps {
    master: SourcesMaster;
    objectName: string;
    config: SourcesConfig;
    onPreviewDocument?: (docId: string) => void;
}

export const SourceMasterRow: React.FC<SourceMasterRowProps> = ({ master, objectName, config, onPreviewDocument }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as unknown as T;
    if ('origin' in master) {
        const { document_id: docId, created_at: createdAt } = master.origin;
        const meta = !docId
            ? t('farm.sources.meta.originManual', 'in hofsync angelegt')
            : createdAt
              ? t('farm.sources.meta.originCreated', 'angelegt am {{date}} aus einem Beleg', { date: formatDateString(createdAt) })
              : t('farm.sources.meta.originFromDocument', 'aus einem Beleg angelegt');
        return (
            <Cells
                kind="origin"
                label="hofsync"
                value={objectName}
                meta={meta}
                trailing={
                    docId && onPreviewDocument ? (
                        <Action onClick={() => onPreviewDocument(docId)}>
                            {t('farm.sources.actions.openDocument', 'Beleg öffnen')} <span aria-hidden="true">↗</span>
                        </Action>
                    ) : null
                }
            />
        );
    }
    const role = master.role_label || config.master_role || t('farm.sources.masterRole', 'Führendes System');
    const earlier = master.earlier_names ?? [];
    return (
        <Cells
            kind="master"
            label={master.source_system ?? t('farm.sources.masterRole', 'Führendes System')}
            value={master.name || objectName}
            meta={role}
            meta2={
                earlier.length
                    ? t('farm.sources.meta.earlierNames', 'früher: {{names}}', { names: earlier.join(', ') })
                    : null
            }
            trailing={
                master.open_url ? (
                    <a className="sources-row__action" href={master.open_url} target="_blank" rel="noopener noreferrer">
                        {t('farm.sources.actions.open', 'Öffnen')} <span aria-hidden="true">↗</span>
                    </a>
                ) : null
            }
        />
    );
};

// --- mapping / alias / duplicate rows --------------------------------------

export interface SourceRowProps {
    row: SourceRowDto;
    config: SourcesConfig;
    /** A season range that ended before this year — rendered dimmed. */
    dimmed?: boolean;
    /** `unassigned` rows: the stub's first sighting (`object.stub.first_seen`), when known. */
    firstSeen?: string | null;
    /** `merge_suggested` rows: the duplicate's entity id (the merge loser). */
    mergeCandidateId?: string;
    busy?: boolean;
    /** The last failed action on this row (reject / merge), already translated. */
    error?: string | null;
    onOpenEditor: (intent: { mode: SourceEditorMode; row?: SourceRowDto }) => void;
    onOpenGroup: (row: SourceRowDto) => void;
    onReject: (row: SourceRowDto) => void;
    /** `done` runs only when the merge succeeded — on failure the inline confirm stays open. */
    onMerge?: (loserId: string, done: () => void) => void;
}

export const SourceRow: React.FC<SourceRowProps> = ({
    row,
    config,
    dimmed,
    firstSeen,
    mergeCandidateId,
    busy,
    error,
    onOpenEditor,
    onOpenGroup,
    onReject,
    onMerge,
}) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as unknown as T;
    const evidence = useEvidence(row.evidence);
    const [confirmingMerge, setConfirmingMerge] = React.useState(false);
    const validity = validityPhrase(row, t);
    const counts = countsPhrase(row.counts, t, i18n.language);
    const system = row.source_system ?? '';
    const change = (
        <Action onClick={() => onOpenEditor({ mode: 'change', row })}>{t('farm.sources.actions.change', 'Ändern')}</Action>
    );
    const reject = (
        <Action onClick={() => onReject(row)} disabled={busy}>
            {t('farm.sources.actions.reject', 'Ablehnen')}
        </Action>
    );

    switch (row.kind) {
        case 'confirmed':
            return (
                <Cells
                    kind="confirmed"
                    label={system}
                    value={row.source_label}
                    meta={joinMeta([validity, counts])}
                    trailing={change}
                    dimmed={dimmed}
                />
            );
        case 'shared': {
            const names = (row.members ?? []).map((m) => m.name).filter(Boolean);
            return (
                <Cells
                    kind="shared"
                    label={system}
                    value={row.source_label}
                    meta={joinMeta([
                        validity,
                        names.length > 0 && t('farm.sources.meta.sharedWith', 'geteilt mit {{names}}', { names: names.join(', ') }),
                    ])}
                    trailing={
                        <Action onClick={() => onOpenGroup(row)}>{t('farm.sources.actions.changeGroup', 'Gruppe ändern')}</Action>
                    }
                    dimmed={dimmed}
                />
            );
        }
        case 'suggested':
            return (
                <Cells
                    kind="suggested"
                    error={error}
                    label={system}
                    value={row.source_label}
                    meta={joinMeta([validity, counts])}
                    meta2={evidence}
                    trailing={
                        <>
                            <SourceStatusBadge variant="suggested">{t('farm.sources.badge.suggested', 'Vorschlag')}</SourceStatusBadge>
                            <Action primary onClick={() => onOpenEditor({ mode: 'accept', row })}>
                                {t('farm.sources.actions.accept', 'Übernehmen')}
                            </Action>
                            {reject}
                        </>
                    }
                />
            );
        case 'unassigned':
            return (
                <Cells
                    kind="unassigned"
                    label={system}
                    value={row.source_label}
                    meta={joinMeta([
                        validity,
                        config.noun
                            ? t('farm.sources.meta.noTarget', 'kein {{noun}} zugeordnet', { noun: config.noun })
                            : t('farm.sources.meta.noTargetGeneric', 'noch nicht zugeordnet'),
                        firstSeen && t('farm.sources.meta.firstSeen', 'zuerst gesehen {{date}}', { date: formatDateString(firstSeen) }),
                    ])}
                    trailing={
                        <>
                            <SourceStatusBadge variant="unassigned">
                                {t('farm.sources.badge.unassigned', 'nicht zugeordnet')}
                            </SourceStatusBadge>
                            <Action primary onClick={() => onOpenEditor({ mode: 'assign', row })}>
                                {t('farm.sources.actions.assign', 'Zuordnen')} <span aria-hidden="true">↑</span>
                            </Action>
                        </>
                    }
                />
            );
        case 'alias':
            // Read-only: an alias is a spelling with no mapping row to change (T12 fix round 1). One a
            // source system wrote (a FruitForecast or older MyFarm24 name) is labelled with that system;
            // only a spelling read off documents says „Belege“. Not dimmed: it is a live name of this
            // object, just one no mapping row decides.
            return (
                <Cells
                    kind="alias"
                    label={system || t('farm.sources.aliasLabel', 'Belege')}
                    value={row.source_label}
                    meta={counts || t('farm.sources.meta.otherSpelling', 'andere Schreibweise')}
                />
            );
        case 'merge_suggested':
            return (
                <Cells
                    kind="merge_suggested"
                    error={error}
                    label={t('farm.sources.aliasLabel', 'Belege')}
                    value={row.source_label}
                    meta={counts}
                    meta2={
                        confirmingMerge
                            ? t(
                                  'farm.sources.merge.question',
                                  '„{{name}}“ hierher zusammenführen? Das lässt sich nicht rückgängig machen.',
                                  { name: row.source_label },
                              )
                            : evidence
                    }
                    trailing={
                        confirmingMerge ? (
                            <>
                                <Action
                                    primary
                                    disabled={busy}
                                    onClick={() => {
                                        if (mergeCandidateId && onMerge) {
                                            onMerge(mergeCandidateId, () => setConfirmingMerge(false));
                                        }
                                    }}
                                >
                                    {t('farm.sources.actions.confirmMerge', 'Zusammenführen')}
                                </Action>
                                <Action onClick={() => setConfirmingMerge(false)}>
                                    {t('farm.sources.actions.cancel', 'Abbrechen')}
                                </Action>
                            </>
                        ) : (
                            <>
                                <SourceStatusBadge variant="suggested">{t('farm.sources.badge.suggested', 'Vorschlag')}</SourceStatusBadge>
                                {mergeCandidateId && onMerge ? (
                                    <Action primary onClick={() => setConfirmingMerge(true)} disabled={busy}>
                                        {t('farm.sources.actions.mergeHere', 'Hierher zusammenführen')}
                                    </Action>
                                ) : null}
                                {reject}
                            </>
                        )
                    }
                />
            );
        default:
            return null;
    }
};

export default SourceRow;
